import { editorUrl } from '@/lib/editorBasePath';
import React, { useEffect, useState } from 'react';
import { Users, Loader2, ChevronRight, ChevronLeft, Trash2, Plus, Settings2 } from 'lucide-react';
import { YearbookPage } from '@/types/yearbook';
import TrombiPlaceDialog from './TrombiPlaceDialog';
import AddSectionDialog from './AddSectionDialog';
import EditTrombiDialog from './EditTrombiDialog';

interface TrombiSection {
  id: number;
  in_book: boolean;
  name: string;
  all_users_count: number;
  page_id: number | null;
  nb_page: number;
  trombi_type: number;
  trombi_print_name?: number | null;
  trombi_style_name?: number | null;
  trombi_order_name?: number | null;
  name_color?: string | null;
  beak_name?: boolean | null;
}

interface TrombiPanelProps {
  pages: YearbookPage[];
  onTrombiCreated: (data: Record<string, { objects_positions: any[] }>) => void;
  onTrombiRemoved: (pageIds: number[]) => void;
  onTrombiUpdated: (data: Record<string, { objects_positions: any[] }>) => void;
  favColors?: string[];
}

const TrombiPanel: React.FC<TrombiPanelProps> = ({ pages, onTrombiCreated, onTrombiRemoved, onTrombiUpdated, favColors = [] }) => {
  const [dialogSection, setDialogSection] = useState<TrombiSection | null>(null);
  const [detailSection, setDetailSection] = useState<TrombiSection | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [sections, setSections] = useState<TrombiSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(editorUrl('/load_trombis'))
      .then((res) => {
        if (!res.ok) throw new Error('Erreur de chargement');
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) throw new Error('Réponse non-JSON');
        return res.json();
      })
      .then((data) => {
        const list: TrombiSection[] = data.sections ?? [];
        setSections(list);
      })
      .catch((err) => {
        console.warn('Fallback données fictives:', err.message);
        setSections([
          { id: 1, in_book: true, name: 'CM2 - Mme Dupont', all_users_count: 28, page_id: 3, nb_page: 2, trombi_type: 1 },
          { id: 2, in_book: true, name: 'CM1 - M. Martin', all_users_count: 25, page_id: 5, nb_page: 1, trombi_type: 2 },
          { id: 3, in_book: false, name: 'CE2 - Mme Bernard', all_users_count: 22, page_id: null, nb_page: 1, trombi_type: 1 },
          { id: 4, in_book: false, name: 'CE1 - M. Petit', all_users_count: 26, page_id: null, nb_page: 1, trombi_type: 2 },
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleRemoveTrombi = async (section: TrombiSection) => {
    if (!section.page_id) return;
    setRemoving(true);
    try {
      const res = await fetch(editorUrl('/remove_trombi_to_yearbook'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary_bloc_id: section.page_id }),
      });
      if (!res.ok) throw new Error('Erreur');
      const pageIds: number[] = await res.json();
      // Reset pages in editor
      onTrombiRemoved(pageIds);
      // Move section back to "Non placées"
      setSections((prev) =>
        prev.map((s) =>
          s.id === section.id ? { ...s, in_book: false, page_id: null } : s
        )
      );
      setDetailSection(null);
    } catch (err) {
      console.error('Failed to remove trombi:', err);
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-muted-foreground" size={20} />
      </div>
    );
  }

  if (error) {
    return <p className="text-xs text-destructive py-4 text-center">{error}</p>;
  }

  const handleSectionCreated = (s: { id: number; name: string; trombi_type: number; all_users_count?: number }) => {
    setSections((prev) => [
      ...prev,
      {
        id: s.id,
        name: s.name,
        trombi_type: s.trombi_type,
        all_users_count: s.all_users_count ?? 0,
        in_book: false,
        page_id: null,
        nb_page: 1,
      },
    ]);
  };

  const addButton = (
    <button
      onClick={() => setAddOpen(true)}
      title="Ajouter une classe"
      className="p-1 rounded-md border border-editor-sidebar-border text-muted-foreground hover:text-editor-sidebar-active hover:border-editor-sidebar-active/40 transition-colors"
    >
      <Plus size={14} />
    </button>
  );

  if (sections.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-center gap-1.5">
          <Users size={14} className="text-editor-sidebar-active" />
          <h3 className="text-sm font-medium text-editor-sidebar-foreground">Trombinoscope</h3>
          {addButton}
        </div>
        <p className="text-xs text-muted-foreground text-center py-4">Aucune classe trouvée.</p>
        <AddSectionDialog open={addOpen} onOpenChange={setAddOpen} onCreated={handleSectionCreated} />
      </div>
    );
  }


  // Detail sub-panel for a section in the book
  if (detailSection) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDetailSection(null)}
            className="p-1 rounded hover:bg-editor-sidebar-hover text-muted-foreground hover:text-editor-sidebar-foreground transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <h3 className="text-sm font-medium text-editor-sidebar-foreground truncate flex-1">{detailSection.name}</h3>
        </div>

        <div className="rounded-lg border border-editor-sidebar-border bg-editor-sidebar-hover/50 p-3 space-y-1">
          <p className="text-xs text-muted-foreground">
            {detailSection.nb_page > 1
              ? `Placé sur les pages ${detailSection.page_id} à ${detailSection.page_id! + detailSection.nb_page - 1}`
              : `Placé sur la page ${detailSection.page_id}`}
          </p>
        </div>

        <button
          onClick={() => setEditOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-editor-sidebar-border bg-editor-sidebar-hover/50 text-editor-sidebar-foreground text-xs font-medium hover:border-editor-sidebar-active/40 transition-colors"
        >
          <Settings2 size={14} />
          Personnaliser le trombi
        </button>

        {detailSection.page_id !== null && (
          <EditTrombiDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            pageId={detailSection.page_id}
            sectionName={detailSection.name}
            favColors={favColors}
            initialPrintName={detailSection.trombi_print_name ?? undefined}
            initialStyleName={detailSection.trombi_style_name ?? undefined}
            initialOrderName={detailSection.trombi_order_name ?? undefined}
            initialNameColor={detailSection.name_color ?? null}
            initialBeakName={detailSection.beak_name ?? undefined}
            onTrombiUpdated={onTrombiUpdated}
            onSettingsSaved={(settings) => {
              setSections((prev) =>
                prev.map((s) => (s.id === detailSection.id ? { ...s, ...settings } : s))
              );
              setDetailSection((prev) => (prev ? { ...prev, ...settings } : prev));
            }}
          />
        )}


        <button
          onClick={() => handleRemoveTrombi(detailSection)}
          disabled={removing}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-xs font-medium hover:bg-destructive/20 transition-colors disabled:opacity-50"
        >
          {removing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          Enlever le trombi du livre
        </button>
      </div>
    );
  }

  const inBook = sections.filter((s) => s.in_book);
  const notInBook = sections.filter((s) => !s.in_book);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center gap-1.5">
        <Users size={14} className="text-editor-sidebar-active" />
        <h3 className="text-sm font-medium text-editor-sidebar-foreground">Trombinoscope</h3>
        {addButton}
      </div>


      {notInBook.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">À placer</p>
          {notInBook.map((section) => (
            <div
              key={section.id}
              onClick={() => setDialogSection(section)}
              className="group rounded-lg border border-editor-sidebar-border bg-editor-sidebar-hover/50 p-2.5 cursor-pointer hover:border-editor-sidebar-active/40 transition-all"
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-editor-sidebar-foreground block truncate">{section.name}</span>
                  <span className="text-[10px] text-muted-foreground mt-0.5 block">
                    {section.all_users_count} élèves · {section.trombi_type === 2 ? 'Trombi complet' : 'Trombi simple'}
                  </span>
                </div>
                <ChevronRight
                  size={14}
                  className="text-muted-foreground group-hover:text-editor-sidebar-active group-hover:translate-x-0.5 transition-all shrink-0"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {inBook.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Dans le livre</p>
          {inBook.map((section) => (
            <div
              key={section.id}
              onClick={() => setDetailSection(section)}
              className="group rounded-lg border border-primary/20 bg-primary/5 p-2.5 cursor-pointer hover:border-primary/40 transition-all"
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-editor-sidebar-foreground block truncate">{section.name}</span>
                  <span className="text-[10px] text-muted-foreground mt-0.5 block">
                    {section.all_users_count} élèves · {section.trombi_type === 2 ? 'Trombi complet' : 'Trombi simple'} · {section.nb_page > 1
                      ? `page ${section.page_id} à ${section.page_id! + section.nb_page - 1}`
                      : `page ${section.page_id}`}
                  </span>
                </div>
                <ChevronRight
                  size={14}
                  className="text-muted-foreground group-hover:text-editor-sidebar-active group-hover:translate-x-0.5 transition-all shrink-0"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {dialogSection && (
        <TrombiPlaceDialog
          open={!!dialogSection}
          onOpenChange={(open) => !open && setDialogSection(null)}
          sectionId={dialogSection.id}
          sectionName={dialogSection.name}
          studentCount={dialogSection.all_users_count}
          pages={pages}
          onTrombiCreated={(data) => {
            onTrombiCreated(data);
            const pageNum = Object.keys(data).map(Number)[0] ?? null;
            setSections((prev) =>
              prev.map((s) =>
                s.id === dialogSection.id
                  ? { ...s, in_book: true, page_id: pageNum }
                  : s
              )
            );
          }}
        />
      )}

      <AddSectionDialog open={addOpen} onOpenChange={setAddOpen} onCreated={handleSectionCreated} />
    </div>

  );
};

export default TrombiPanel;
